import { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import {
  Heart, Mail, Lock, User, ArrowLeft, Shield, RefreshCw, Loader2,
  VenetianMask, KeyRound, Copy, Download, AlertTriangle
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";

const formatAccountInput = (value) => {
  const digits = value.replace(/\D/g, "").slice(0, 22);
  return digits.replace(/(\d{4})(?=\d)/g, "$1 ");
};

const AuthPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [twoFactor, setTwoFactor] = useState(null); // { pending_id } while an admin sign-in waits for its emailed code
  const [twoFactorCode, setTwoFactorCode] = useState("");

  const [loginData, setLoginData] = useState({ email: "", password: "" });
  const [registerData, setRegisterData] = useState({
    email: "",
    password: "",
    name: ""
  });

  // Verification code state
  const [showVerification, setShowVerification] = useState(false);
  const [displayedCode, setDisplayedCode] = useState("");
  const [pendingUser, setPendingUser] = useState(null);
  const [enteredCode, setEnteredCode] = useState("");

  // Anonymous account state
  const [view, setView] = useState(() => (new URLSearchParams(window.location.search).get("view") === "anonymous" ? "anonymous" : "standard")); // standard | anonymous
  const [anonLoginNumber, setAnonLoginNumber] = useState("");
  const [anonPending, setAnonPending] = useState(null); // { user, token, account_number }
  const [anonSaved, setAnonSaved] = useState(false);
  const [anonHoneypot, setAnonHoneypot] = useState(""); // hidden field — bots fill it, humans never see it

  const from = new URLSearchParams(location.search).get("next") || location.state?.from?.pathname || "/dashboard";

  const handleLogin = async (e) => {
    e.preventDefault();
    const email = (loginData.email || "").trim();
    const password = loginData.password || "";
    if (!email || !password) {
      toast.error("Please enter both your email and password.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error("That email looks incomplete — include the full address (e.g. name@domain.com).");
      return;
    }
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password }, {
        withCredentials: true
      });
      if (response.data.two_factor_required) {
        setTwoFactor({ pending_id: response.data.pending_id });
        toast.success("Check your email for a 6-digit sign-in code");
        return;
      }
      finishLogin(response);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const handleTwoFactor = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login/verify-code`, { pending_id: twoFactor.pending_id, code: twoFactorCode.trim(), trust_device: true }, { withCredentials: true });
      setTwoFactor(null); setTwoFactorCode("");
      finishLogin(response);
    } catch (error) {
      toast.error(error.response?.data?.detail || "That code didn't work");
    } finally {
      setLoading(false);
    }
  };

  const finishLogin = (response) => {
    {
      login(response.data.user, response.data.token);
      toast.success("Welcome back!");
      const u = response.data.user || {};
      const editorHome = u.role === "editor" && (u.editor_pages || [])[0]?.startsWith("festival:")
        ? `/festival/${u.editor_pages[0].split(":")[1]}` : null;
      navigate(editorHome && from === "/dashboard" ? editorHome : from, { replace: true });
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/register`, { ...registerData, ref: localStorage.getItem("wall_ref") || undefined });

      setPendingUser({
        user_id: response.data.user_id,
        email: registerData.email,
        password: registerData.password
      });
      setDisplayedCode(response.data.verification_code);
      setShowVerification(true);
      toast.success("Account created! Enter the verification code to continue.");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    if (enteredCode.length !== 6) {
      toast.error("Please enter the 6-digit code");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/verify-code?user_id=${pendingUser.user_id}&code=${enteredCode}`);

      const loginResponse = await axios.post(`${API}/auth/login`, {
        email: pendingUser.email,
        password: pendingUser.password
      }, { withCredentials: true });

      login(loginResponse.data.user, loginResponse.data.token);
      toast.success("Welcome to THE WALL!");
      navigate(from, { replace: true });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid code");
      if (error.response?.data?.detail?.includes("expired")) {
        try {
          const refreshResponse = await axios.post(`${API}/auth/refresh-code?user_id=${pendingUser.user_id}`);
          setDisplayedCode(refreshResponse.data.verification_code);
          setEnteredCode("");
        } catch (e) {
          // Error handled silently
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRefreshCode = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/refresh-code?user_id=${pendingUser.user_id}`);
      setDisplayedCode(response.data.verification_code);
      setEnteredCode("");
      toast.success("New code generated!");
    } catch (error) {
      toast.error("Failed to generate new code");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleAnonymousCreate = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/anonymous/create`, { website: anonHoneypot }, { withCredentials: true });
      setAnonPending(response.data);
      setAnonSaved(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create anonymous account");
    } finally {
      setLoading(false);
    }
  };

  const handleAnonymousLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/anonymous/login`,
        { account_number: anonLoginNumber },
        { withCredentials: true }
      );
      login(response.data.user, response.data.token);
      toast.success("Welcome back!");
      navigate(from, { replace: true });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const handleAnonymousEnter = () => {
    login(anonPending.user, anonPending.token);
    toast.success("Welcome to THE WALL!");
    navigate(from, { replace: true });
  };

  const copyAnonNumber = () => {
    navigator.clipboard.writeText(anonPending.account_number);
    toast.success("Copied to clipboard");
  };

  const downloadAnonNumber = () => {
    const blob = new Blob(
      [`THE WALL — Anonymous Account Number\n\n${anonPending.account_number}\n\nThis number is your only key. It cannot be recovered if lost.\nSign in at: ${window.location.origin}/auth\n`],
      { type: "text/plain" }
    );
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "the-wall-account-number.txt";
    a.click();
    URL.revokeObjectURL(url);
  };

  // ────────── One-time "save your number" screen ──────────
  if (anonPending) {
    return (
      <div className="min-h-screen bg-[#F9F8F6] flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <Card className="border-[#E2E0DC]">
            <CardHeader className="text-center">
              <div className="flex justify-center mb-4">
                <VenetianMask className="h-12 w-12 text-[#2E5C55]" />
              </div>
              <CardTitle className="font-['Playfair_Display'] text-2xl">Your Account Number</CardTitle>
              <CardDescription className="text-[#5C5C5C] mt-2">
                This is your only key to this account. It will never be shown again.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="bg-gradient-to-r from-[#2E5C55]/10 to-[#E76F51]/10 rounded-xl p-6 text-center">
                <div className="font-mono text-2xl sm:text-3xl font-bold tracking-widest text-[#2E5C55]" data-testid="anon-account-number">
                  {anonPending.account_number}
                </div>
              </div>

              <div className="flex gap-3 justify-center">
                <Button variant="outline" className="rounded-full" onClick={copyAnonNumber} data-testid="anon-copy-btn">
                  <Copy className="h-4 w-4 mr-2" /> Copy
                </Button>
                <Button variant="outline" className="rounded-full" onClick={downloadAnonNumber} data-testid="anon-download-btn">
                  <Download className="h-4 w-4 mr-2" /> Download .txt
                </Button>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex gap-3">
                <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
                <p className="text-sm text-amber-800">
                  If you lose this number, your account <strong>cannot be recovered</strong>.
                  We store no email, no name, no personal information — that's the point.
                </p>
              </div>

              <div className="flex items-start gap-3">
                <Checkbox
                  id="anon-saved"
                  checked={anonSaved}
                  onCheckedChange={(v) => setAnonSaved(!!v)}
                  data-testid="anon-saved-checkbox"
                />
                <Label htmlFor="anon-saved" className="text-sm leading-snug cursor-pointer">
                  I've saved my account number somewhere safe
                </Label>
              </div>

              <Button
                onClick={handleAnonymousEnter}
                disabled={!anonSaved}
                className="w-full bg-[#2E5C55] hover:bg-[#2E5C55]/90 rounded-full"
                data-testid="anon-enter-btn"
              >
                Enter THE WALL
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // ────────── Verification code screen (email register) ──────────
  if (showVerification) {
    return (
      <div className="min-h-screen bg-[#F9F8F6] flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <Card className="border-[#E2E0DC]">
            <CardHeader className="text-center">
              <div className="flex justify-center mb-4">
                <Shield className="h-12 w-12 text-[#2E5C55]" />
              </div>
              <CardTitle className="font-['Playfair_Display'] text-2xl">Verify Your Account</CardTitle>
              <CardDescription className="text-[#5C5C5C] mt-2">
                Enter the code below to complete your registration
              </CardDescription>
            </CardHeader>

            <CardContent className="space-y-6">
              <div className="bg-gradient-to-r from-[#2E5C55]/10 to-[#E76F51]/10 rounded-xl p-6 text-center">
                <p className="text-sm text-[#5C5C5C] mb-2">Your verification code:</p>
                <div className="font-mono text-4xl font-bold tracking-[0.5em] text-[#2E5C55]">
                  {displayedCode}
                </div>
                <p className="text-xs text-[#5C5C5C] mt-3">Code expires in 15 minutes</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="verification-code">Enter the code above:</Label>
                <Input
                  id="verification-code"
                  type="text"
                  maxLength={6}
                  value={enteredCode}
                  onChange={(e) => setEnteredCode(e.target.value.replace(/\D/g, ''))}
                  placeholder="000000"
                  className="text-center text-2xl tracking-[0.3em] font-mono"
                  data-testid="verification-code-input"
                />
              </div>

              <Button
                onClick={handleVerifyCode}
                className="w-full bg-[#2E5C55] hover:bg-[#2E5C55]/90"
                disabled={loading || enteredCode.length !== 6}
                data-testid="verify-code-submit"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Verifying...
                  </>
                ) : (
                  "Verify & Continue"
                )}
              </Button>

              <div className="text-center">
                <button
                  type="button"
                  onClick={handleRefreshCode}
                  className="text-sm text-[#5C5C5C] hover:text-[#2E5C55] inline-flex items-center"
                  disabled={loading}
                >
                  <RefreshCw className="h-3 w-3 mr-1" />
                  Generate new code
                </button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // ────────── Anonymous mode (sign in / create) ──────────
  if (view === "anonymous") {
    return (
      <div className="min-h-screen bg-[#F9F8F6] flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <button
            onClick={() => setView("standard")}
            className="inline-flex items-center text-[#5C5C5C] hover:text-[#2E5C55] mb-8 transition-colors"
            data-testid="back-to-standard-auth"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to standard sign in
          </button>

          <Card className="border-[#E2E0DC]">
            <CardHeader className="text-center">
              <div className="flex justify-center mb-4">
                <VenetianMask className="h-12 w-12 text-[#2E5C55]" />
              </div>
              <CardTitle className="font-['Playfair_Display'] text-3xl">Anonymous Access</CardTitle>
              <CardDescription className="text-[#5C5C5C] mt-2">
                No email. No name. No personal info. Just a 16-digit number.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Tabs defaultValue={new URLSearchParams(window.location.search).get("tab") === "create" ? "anon-create" : "anon-login"} className="w-full">
                <TabsList className="grid w-full grid-cols-2 mb-6">
                  <TabsTrigger value="anon-login" data-testid="anon-login-tab">Sign In</TabsTrigger>
                  <TabsTrigger value="anon-create" data-testid="anon-create-tab">New Account</TabsTrigger>
                </TabsList>

                <TabsContent value="anon-login">
                  <form onSubmit={handleAnonymousLogin} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="anon-number">Account number</Label>
                      <div className="relative">
                        <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#5C5C5C]" />
                        <Input
                          id="anon-number"
                          type="text"
                          inputMode="numeric"
                          placeholder="Your 16-digit number or access key"
                          value={anonLoginNumber}
                          onChange={(e) => setAnonLoginNumber(formatAccountInput(e.target.value))}
                          autoComplete="off"
                          className="pl-10 font-mono tracking-wider"
                          required
                          data-testid="anon-number-input"
                        />
                      </div>
                    </div>
                    <Button
                      type="submit"
                      className="w-full bg-[#2E5C55] hover:bg-[#2E5C55]/90 rounded-full"
                      disabled={loading || ![16, 22].includes(anonLoginNumber.replace(/\D/g, "").length)}
                      data-testid="anon-login-submit"
                    >
                      {loading ? "Signing in..." : "Sign In"}
                    </Button>
                  </form>
                </TabsContent>

                <TabsContent value="anon-create">
                  <div className="space-y-4">
                    <input
                      type="text"
                      name="website"
                      value={anonHoneypot}
                      onChange={(e) => setAnonHoneypot(e.target.value)}
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      style={{ position: "absolute", left: "-9999px", height: 0, width: 0, opacity: 0 }}
                    />
                    <ul className="text-sm text-[#5C5C5C] space-y-2 bg-[#F9F8F6] p-4 rounded-lg">
                      <li>• We generate a random 16-digit account number for you</li>
                      <li>• That number is your <strong>only</strong> credential — treat it like a key</li>
                      <li>• If you lose it, the account <strong>cannot be recovered</strong></li>
                      <li>• Nothing about you is stored — a breach can never leak what we never had</li>
                    </ul>
                    <Button
                      onClick={handleAnonymousCreate}
                      className="w-full bg-[#2E5C55] hover:bg-[#2E5C55]/90 rounded-full"
                      disabled={loading}
                      data-testid="anon-create-submit"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Generating...
                        </>
                      ) : (
                        "Generate my account number"
                      )}
                    </Button>
                  </div>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // ────────── Standard view (Google + email + anonymous entry point) ──────────
  return (
    <div className="min-h-screen bg-[#F9F8F6] flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        {/* Back to Home */}
        <Link to="/" className="inline-flex items-center text-[#5C5C5C] hover:text-[#2E5C55] mb-8 transition-colors" data-testid="back-to-home">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Home
        </Link>

        <Card className="border-[#E2E0DC]">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <Heart className="h-12 w-12 text-[#2E5C55]" fill="#2E5C55" />
            </div>
            <CardTitle className="font-['Playfair_Display'] text-3xl">
              <a
                href="https://www.stars4peace.org/us.html#/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#2E5C55] hover:underline"
                data-testid="artists-link"
              >
                Artists
              </a>{" "}
              welcome ALL
              <br />
              to THE WALL
            </CardTitle>
            <p className="text-[#E76F51] font-medium mt-1">This is the Power of Peace to Provide.</p>
            <CardDescription className="text-[#5C5C5C] mt-2">
              Sign in & help fund a mental change
            </CardDescription>
          </CardHeader>
          <CardContent>
            {/* Google OAuth */}
            <Button
              variant="outline"
              className="w-full mb-4 rounded-full border-[#E2E0DC] hover:bg-[#F9F8F6]"
              onClick={handleGoogleLogin}
              data-testid="google-login-btn"
            >
              <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continue with Google
            </Button>

            {/* Anonymous entry point */}
            <button
              onClick={() => setView("anonymous")}
              className="w-full mb-6 text-left border border-[#2E5C55]/30 bg-[#2E5C55]/5 hover:bg-[#2E5C55]/10 rounded-xl p-4 transition-colors group"
              data-testid="anonymous-mode-btn"
            >
              <div className="flex items-start gap-3">
                <VenetianMask className="h-6 w-6 text-[#2E5C55] shrink-0 mt-0.5" />
                <div>
                  <span className="font-medium text-[#1A1A1A] group-hover:text-[#2E5C55] transition-colors">
                    Prefer to stay anonymous? →
                  </span>
                  <p className="text-xs text-[#5C5C5C] mt-1">
                    Sign in with a 16-digit account number instead. No email, no name,
                    no personal info — but if you lose your number, the account can't be recovered.
                  </p>
                </div>
              </div>
            </button>

            <div className="relative mb-6">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t border-[#E2E0DC]" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-white px-2 text-[#5C5C5C]">Or continue with email</span>
              </div>
            </div>

            <Tabs defaultValue="login" className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-6">
                <TabsTrigger value="login" data-testid="login-tab">Sign In</TabsTrigger>
                <TabsTrigger value="register" data-testid="register-tab">Register</TabsTrigger>
              </TabsList>

              <TabsContent value="login">
                {twoFactor ? (
                <form onSubmit={handleTwoFactor} noValidate className="space-y-4" data-testid="two-factor-form">
                  <p className="text-sm text-[#3a3a3a]">Admin accounts need a second step. We emailed you a 6-digit sign-in code — it expires in 10 minutes.</p>
                  <Input value={twoFactorCode} onChange={(e) => setTwoFactorCode(e.target.value)} inputMode="numeric" maxLength={6} placeholder="6-digit code" autoFocus className="text-center tracking-[0.5em] text-lg" data-testid="two-factor-code" />
                  <Button type="submit" disabled={loading || twoFactorCode.trim().length !== 6} className="w-full rounded-full bg-[#1A1A1A] text-white" data-testid="two-factor-submit">{loading ? "Checking…" : "Finish sign-in"}</Button>
                  <button type="button" onClick={() => setTwoFactor(null)} className="w-full text-xs text-[#5C5C5C] underline" data-testid="two-factor-cancel">Start over</button>
                </form>
                ) : (
                <form onSubmit={handleLogin} noValidate className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="login-email">Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#5C5C5C]" />
                      <Input
                        id="login-email"
                        type="email"
                        placeholder="you@example.com"
                        value={loginData.email}
                        onChange={(e) => setLoginData({...loginData, email: e.target.value})}
                        className="pl-10"
                        required
                        data-testid="login-email"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="login-password">Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#5C5C5C]" />
                      <Input
                        id="login-password"
                        type="password"
                        placeholder="••••••••"
                        value={loginData.password}
                        onChange={(e) => setLoginData({...loginData, password: e.target.value})}
                        className="pl-10"
                        required
                        data-testid="login-password"
                      />
                    </div>
                    <div className="text-right">
                      <Link to="/forgot-password" className="text-sm text-[#2E5C55] hover:underline">
                        Forgot password?
                      </Link>
                    </div>
                  </div>
                  <Button
                    type="submit"
                    className="w-full bg-[#2E5C55] hover:bg-[#2E5C55]/90 rounded-full"
                    disabled={loading}
                    data-testid="login-submit"
                  >
                    {loading ? "Signing in..." : "Sign In"}
                  </Button>
                </form>
                )}
              </TabsContent>

              <TabsContent value="register">
                <form onSubmit={handleRegister} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="register-name">Full Name</Label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#5C5C5C]" />
                      <Input
                        id="register-name"
                        type="text"
                        placeholder="Your name"
                        value={registerData.name}
                        onChange={(e) => setRegisterData({...registerData, name: e.target.value})}
                        className="pl-10"
                        required
                        data-testid="register-name"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="register-email">Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#5C5C5C]" />
                      <Input
                        id="register-email"
                        type="email"
                        placeholder="you@example.com"
                        value={registerData.email}
                        onChange={(e) => setRegisterData({...registerData, email: e.target.value})}
                        className="pl-10"
                        required
                        data-testid="register-email"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="register-password">Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#5C5C5C]" />
                      <Input
                        id="register-password"
                        type="password"
                        placeholder="••••••••"
                        value={registerData.password}
                        onChange={(e) => setRegisterData({...registerData, password: e.target.value})}
                        className="pl-10"
                        required
                        minLength={6}
                        data-testid="register-password"
                      />
                    </div>
                  </div>
                  <p className="text-sm text-[#5C5C5C] bg-[#F9F8F6] p-3 rounded-lg">
                    Want to contribute music, art, or deals? You can add contributor roles from your StarGate after signing up.
                  </p>
                  <Button
                    type="submit"
                    className="w-full bg-[#2E5C55] hover:bg-[#2E5C55]/90 rounded-full"
                    disabled={loading}
                    data-testid="register-submit"
                  >
                    {loading ? "Creating account..." : "Create Account"}
                  </Button>
                </form>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AuthPage;
